// ANY EDITS TO THIS PAGE SHOULD ALSO BE MADE TO ITS DEMO COUNTERPART, TO KEEP THE APP AND DEMO IN SYNC
import './RankAthletesPage.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import AthleteCard from '../components/common/AthleteCard/AthleteCard';
import AthleteSearchBar from '../components/common/AthleteSearchBar/AthleteSearchBar';
import PageTopNav from '../components/common/PageTopNav/PageTopNav';
import RankAthleteDetail from '../components/common/RankAthleteDetail/RankAthleteDetail';
import ScrollToTopFab from '../components/common/ScrollToTopFab/ScrollToTopFab';
import { usePostHog } from '@posthog/react';
import { scoringApp } from '../firebase';
import {
  athleteConverter,
  parseTryoutMeta,
  setAthleteTeamPlacement,
} from '../services/athleteService';
import {
  AthletePosition,
  AthleteWithId,
} from '../types/athlete';
import { FormSchema } from '../types/formSchema';
import { ChevronRightIcon } from './TryoutIcons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RankingTryoutMeta {
  name: string;
  formSchema: FormSchema;
  athletePositions: string[];
  teams: string[];
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready' };

type FilterValue = 'all' | AthletePosition;
type TeamFilterValue = 'all' | 'unassigned' | string;

/** Position counts for one team. `unlisted` holds athletes with no known position. */
interface TeamPositionCounts {
  byPosition: Record<string, number>;
  unlisted: number;
  total: number;
}

const SCROLL_TOP_FAB_THRESHOLD_PX = 180;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface SummarySectionHeaderProps {
  title: string;
  open: boolean;
  onToggle: () => void;
}

/** Collapsible section header inside the summary card. */
const SummarySectionHeader: React.FC<SummarySectionHeaderProps> = ({
  title,
  open,
  onToggle,
}) => (
  <button
    type="button"
    className="rank-athletes-summary-header"
    onClick={onToggle}
    aria-expanded={open}
  >
    <span className="rank-athletes-summary-title">{title}</span>
    <span
      className={`rank-athletes-summary-arrow${open ? ' rank-athletes-summary-arrow--open' : ''}`}
    >
      <ChevronRightIcon size={18} />
    </span>
  </button>
);


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RankAthletesPage: React.FC = () => {
  const { tryoutId } = useParams<{ tryoutId: string }>();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const db = getFirestore(scoringApp);

  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [tryoutMeta, setTryoutMeta] = useState<RankingTryoutMeta>({
    name: '',
    formSchema: { groups: [] },
    athletePositions: [],
    teams: [],
  });
  const [athletes, setAthletes] = useState<AthleteWithId[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState<FilterValue>('all');
  const [teamFilter, setTeamFilter] = useState<TeamFilterValue>('all');
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);
  const [scrollTopFabVisible, setScrollTopFabVisible] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tryoutId) {
      setLoadState({ status: 'error', message: 'Invalid tryout link.' });
      return;
    }

    const unsubs: Array<() => void> = [];

    unsubs.push(
      onSnapshot(
        doc(db, 'tryouts', tryoutId),
        (snap) => {
          if (!snap.exists()) {
            setLoadState({ status: 'error', message: 'Tryout not found.' });
            return;
          }
          const meta = parseTryoutMeta(snap.data());
          setTryoutMeta({
            name: meta.name,
            formSchema: meta.formSchema,
            athletePositions: meta.athletePositions,
            teams: meta.teams,
          });
          setLoadState({ status: 'ready' });
        },
        () => setLoadState({ status: 'error', message: 'Failed to load tryout.' }),
      ),
    );

    unsubs.push(
      onSnapshot(
        collection(db, 'tryouts', tryoutId, 'athletes').withConverter(athleteConverter),
        (snap) => {
          setAthletes(snap.docs.map((d) => d.data()));
        },
        () => { /* athletes load failure is non-fatal; list stays empty */ },
      ),
    );

    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [db, tryoutId]);

  const handleContentScroll = useCallback((): void => {
    const el = contentScrollRef.current;
    if (!el) return;
    const next = el.scrollTop > SCROLL_TOP_FAB_THRESHOLD_PX;
    setScrollTopFabVisible((prev) => (prev === next ? prev : next));
  }, []);

  const scrollContentToTop = useCallback((): void => {
    const el = contentScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const sortedAthletes = useMemo<AthleteWithId[]>(() => {
    return [...athletes].sort((a, b) => {
      const na = parseInt(a.number, 10);
      const nb = parseInt(b.number, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.number.localeCompare(b.number);
    });
  }, [athletes]);

  const isSearching = searchQuery.trim().length > 0;

  const filteredAthletes = useMemo<AthleteWithId[]>(() => {
    let list = sortedAthletes;

    if (!isSearching) {
      if (positionFilter !== 'all') {
        list = list.filter((a) => a.position === positionFilter);
      }

      if (teamFilter === 'unassigned') {
        list = list.filter((a) => !a.teamPlacement);
      } else if (teamFilter !== 'all') {
        list = list.filter((a) => a.teamPlacement === teamFilter);
      }
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.number.toLowerCase().includes(q),
      );
    }

    return list;
  }, [sortedAthletes, positionFilter, teamFilter, searchQuery, isSearching]);

  /** Tryout-configured positions, stable order, trimmed, no duplicates. */
  const tryoutPositions = useMemo<string[]>(() => {
    const seen = new Set<string>();
    const positions: string[] = [];
    for (const raw of tryoutMeta.athletePositions) {
      const p = typeof raw === 'string' ? raw.trim() : '';
      if (p.length > 0 && !seen.has(p)) {
        seen.add(p);
        positions.push(p);
      }
    }
    return positions;
  }, [tryoutMeta.athletePositions]);

  const positionFilterOptions = useMemo<Array<{ label: string; value: FilterValue }>>(
    () => [
      { label: 'All', value: 'all' },
      ...tryoutPositions.map((p) => ({ label: p, value: p })),
    ],
    [tryoutPositions],
  );

  const teamFilterOptions = useMemo<Array<{ label: string; value: TeamFilterValue }>>(() => {
    if (tryoutMeta.teams.length === 0) return [];
    return [
      { label: 'All', value: 'all' },
      ...tryoutMeta.teams.map((t) => ({ label: t, value: t })),
      { label: 'Unassigned', value: 'unassigned' },
    ];
  }, [tryoutMeta.teams]);

  const teamPlacementCounts = useMemo(() => {
    const counts: Record<string, number> = { unassigned: 0 };
    for (const team of tryoutMeta.teams) {
      counts[team] = 0;
    }
    for (const athlete of athletes) {
      if (athlete.teamPlacement && counts[athlete.teamPlacement] !== undefined) {
        counts[athlete.teamPlacement]++;
      } else {
        counts.unassigned++;
      }
    }
    return counts;
  }, [athletes, tryoutMeta.teams]);

  /** Per-team position counts. Every tryout position appears, zero included. */
  const teamPositionCounts = useMemo<Record<string, TeamPositionCounts>>(() => {
    const knownPositions = new Set(tryoutPositions);
    const counts: Record<string, TeamPositionCounts> = {};

    for (const team of tryoutMeta.teams) {
      const byPosition: Record<string, number> = {};
      for (const position of tryoutPositions) {
        byPosition[position] = 0;
      }
      counts[team] = { byPosition, unlisted: 0, total: 0 };
    }

    for (const athlete of athletes) {
      const team = athlete.teamPlacement;
      if (!team) continue;
      const row = counts[team];
      // Skip a placement on a team the tryout no longer lists.
      if (!row) continue;

      const position = typeof athlete.position === 'string' ? athlete.position.trim() : '';
      if (knownPositions.has(position)) {
        row.byPosition[position]++;
      } else {
        row.unlisted++;
      }
      row.total++;
    }

    return counts;
  }, [athletes, tryoutMeta.teams, tryoutPositions]);

  const selectedAthlete = useMemo(
    () => (selectedAthleteId ? (filteredAthletes.find((a) => a.id === selectedAthleteId) ?? null) : null),
    [selectedAthleteId, filteredAthletes],
  );

  const selectedAthleteIndex = useMemo(
    () => (selectedAthleteId ? filteredAthletes.findIndex((a) => a.id === selectedAthleteId) : -1),
    [selectedAthleteId, filteredAthletes],
  );

  const handlePreviousAthlete = useCallback((): void => {
    if (selectedAthleteIndex <= 0) return;
    setSelectedAthleteId(filteredAthletes[selectedAthleteIndex - 1].id);
  }, [filteredAthletes, selectedAthleteIndex]);

  const handleNextAthlete = useCallback((): void => {
    if (selectedAthleteIndex < 0 || selectedAthleteIndex >= filteredAthletes.length - 1) return;
    setSelectedAthleteId(filteredAthletes[selectedAthleteIndex + 1].id);
  }, [filteredAthletes, selectedAthleteIndex]);

  const handleTeamPlacementChange = useCallback(
    (teamPlacement: string | null): void => {
      if (!tryoutId || !selectedAthleteId) return;
      void setAthleteTeamPlacement(db, tryoutId, selectedAthleteId, teamPlacement);
      posthog?.capture('athlete_placed_on_team', { tryout_id: tryoutId, team: teamPlacement });
    },
    [db, tryoutId, selectedAthleteId, posthog],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const header = (
    <PageTopNav
      onBack={() => navigate(`/tryout/${tryoutId}`)}
      backAriaLabel="Back to tryout dashboard"
      title={loadState.status === 'ready' ? `${tryoutMeta.name} Ranking` : ''}
    />
  );

  const renderScrollShell = (body: React.ReactNode): React.ReactElement => (
    <div className="rank-athletes-shell">
      {header}
      <div
        ref={contentScrollRef}
        onScroll={handleContentScroll}
        className="rank-athletes-content"
      >
        {body}
      </div>
      <ScrollToTopFab
        visible={scrollTopFabVisible}
        onPress={scrollContentToTop}
      />
      {selectedAthlete ? (
        <RankAthleteDetail
          athlete={selectedAthlete}
          formSchema={tryoutMeta.formSchema}
          teams={tryoutMeta.teams}
          onClose={() => setSelectedAthleteId(null)}
          onTeamPlacementChange={handleTeamPlacementChange}
          onPrevious={handlePreviousAthlete}
          onNext={handleNextAthlete}
          hasPrevious={selectedAthleteIndex > 0}
          hasNext={selectedAthleteIndex < filteredAthletes.length - 1}
        />
      ) : null}
    </div>
  );

  if (loadState.status === 'loading') {
    return renderScrollShell(
      <>
        <div className="rank-athletes-skeleton rank-athletes-skeleton--search" />
        <div className="rank-athletes-skeleton-filters">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="rank-athletes-skeleton rank-athletes-skeleton--chip" />
          ))}
        </div>
        <div className="rank-athletes-list">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="rank-athletes-skeleton rank-athletes-skeleton--card" />
          ))}
        </div>
      </>,
    );
  }

  if (loadState.status === 'error') {
    return renderScrollShell(
      <p className="rank-athletes-error">{loadState.message}</p>,
    );
  }

  return renderScrollShell(
    <>
      <AthleteSearchBar value={searchQuery} onChange={setSearchQuery} />

      {/* Position filter — hidden while searching */}
      {!isSearching && positionFilterOptions.length > 1 ? (
        <div
          className="rank-athletes-filter-row"
          role="group"
          aria-label="Filter by position"
        >
          {positionFilterOptions.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              className={[
                'rank-athletes-filter-chip',
                positionFilter === value ? 'rank-athletes-filter-chip--active' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setPositionFilter(value)}
              aria-pressed={positionFilter === value}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Team filter — hidden while searching */}
      {!isSearching && teamFilterOptions.length > 0 ? (
        <div
          className="rank-athletes-filter-row"
          role="group"
          aria-label="Filter by team"
        >
          {teamFilterOptions.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              className={[
                'rank-athletes-filter-chip',
                teamFilter === value ? 'rank-athletes-filter-chip--active' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setTeamFilter(value)}
              aria-pressed={teamFilter === value}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Team placement summary — hidden while searching */}
      {!isSearching && tryoutMeta.teams.length > 0 ? (
        <div className="rank-athletes-summary">
          <SummarySectionHeader
            title="Team Placement Summary"
            open={summaryOpen}
            onToggle={() => setSummaryOpen((o) => !o)}
          />
          {summaryOpen ? (
            <div className="rank-athletes-summary-counts">
              {tryoutMeta.teams.map((team) => (
                <div key={team} className="rank-athletes-summary-count">
                  <span className="rank-athletes-summary-num">{teamPlacementCounts[team] ?? 0}</span>
                  <span className="rank-athletes-summary-label">{team}</span>
                </div>
              ))}
              <div className="rank-athletes-summary-count rank-athletes-summary-count--unassigned">
                <span className="rank-athletes-summary-num">{teamPlacementCounts.unassigned ?? 0}</span>
                <span className="rank-athletes-summary-label">Unassigned</span>
              </div>
            </div>
          ) : null}

          {/* Position breakdown — one block per team */}
          {tryoutPositions.length > 0 ? (
            <>
              <SummarySectionHeader
                title="Position Breakdown"
                open={breakdownOpen}
                onToggle={() => setBreakdownOpen((o) => !o)}
              />
              {breakdownOpen ? (
                <div className="rank-athletes-breakdown">
                  {tryoutMeta.teams.map((team) => {
                    const counts = teamPositionCounts[team];
                    return (
                      <div key={team} className="rank-athletes-breakdown-team">
                        <div className="rank-athletes-breakdown-team-head">
                          <span className="rank-athletes-breakdown-team-name">{team}</span>
                          <span className="rank-athletes-breakdown-team-total">
                            {counts?.total ?? 0}
                          </span>
                        </div>
                        <div className="rank-athletes-breakdown-pills">
                          {tryoutPositions.map((position) => {
                            const count = counts?.byPosition[position] ?? 0;
                            return (
                              <span
                                key={position}
                                className={[
                                  'rank-athletes-breakdown-pill',
                                  count === 0 ? 'rank-athletes-breakdown-pill--zero' : '',
                                ].filter(Boolean).join(' ')}
                              >
                                <span className="rank-athletes-breakdown-pill-label">{position}</span>
                                <span className="rank-athletes-breakdown-pill-num">{count}</span>
                              </span>
                            );
                          })}
                          {counts && counts.unlisted > 0 ? (
                            <span className="rank-athletes-breakdown-pill rank-athletes-breakdown-pill--unlisted">
                              <span className="rank-athletes-breakdown-pill-label">No position</span>
                              <span className="rank-athletes-breakdown-pill-num">{counts.unlisted}</span>
                            </span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {/* Athlete list */}
      {filteredAthletes.length === 0 ? (
        <div className="rank-athletes-empty">
          {athletes.length === 0 ? (
            <>
              <p className="rank-athletes-empty-title">No athletes yet</p>
              <p className="rank-athletes-empty-hint">
                Add athletes from the tryout dashboard.
              </p>
            </>
          ) : (
            <p className="rank-athletes-empty-title">No athletes match your search.</p>
          )}
        </div>
      ) : (
        <ul className="rank-athletes-list" aria-label="Athletes">
          {filteredAthletes.map((athlete) => (
            <li key={athlete.id}>
              <AthleteCard
                id={athlete.id}
                name={athlete.name}
                number={athlete.number}
                position={athlete.position}
                returningstatus={athlete.returningstatus}
                imageThumbnailUrl={athlete.imageThumbnailUrl}
                teamPlacement={athlete.teamPlacement ?? undefined}
                onClick={(id) => setSelectedAthleteId(id)}
              />
            </li>
          ))}
        </ul>
      )}
    </>,
  );
};

export default RankAthletesPage;
